import {useEffect, useState} from "react";
import toast from "react-hot-toast";
import Card from "components/card";
import {Link} from "react-router-dom";
import Nft2 from "assets/img/nfts/Nft2.png";
import {Agent} from "types";
import {
    AGENT_RUN_STATUS,
    AGENT_TYPES,
    AUTONOMOUS_AGENT_STATUS,
    CREATE_AGENT_CATEGORIES_OPTIONS,
    FUNCTIONAL_AGENT_TYPE_OPTIONS,
} from "constants/agents";
import {getAgentStatusColor, getAgentStatusTranslationKey} from "utils";
import {Image} from "components/Image";
import {useTranslation} from "hooks/useTranslation";
import {startAgentRun, stopAgentRun, triggerAgentRun} from "api/axios";

interface AgentCardProps {
  extra?: string;
  agent: Agent;
  onScheduleStatusChange?: (
    agentId: string,
    scheduleStatus?: Agent["schedule_status"]
  ) => void;
}

const AgentCard = (props: AgentCardProps) => {
  const { agent, extra, onScheduleStatusChange } = props;
  const isOrchestrator = agent.agent_type === AGENT_TYPES.ORCHESTRATOR;
  const isAutonomous = agent.agent_type === AGENT_TYPES.AUTONOMOUS;
  const { t } = useTranslation();
  const [isTriggering, setIsTriggering] = useState(false);
  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState(false);
  const [currentScheduleStatus, setCurrentScheduleStatus] = useState<
    Agent["schedule_status"]
  >(agent.schedule_status);

  useEffect(() => {
    setCurrentScheduleStatus(agent.schedule_status);
  }, [agent.schedule_status]);

  // Get the display label for agent type
  const getAgentTypeLabel = () => {
    // Check if it's a functional agent type (like AUTONOMOUS)
    const functionalType = FUNCTIONAL_AGENT_TYPE_OPTIONS.find(
      (option) => option.value === (agent.agent_type as any)
    );
    if (functionalType) {
      return t(functionalType.labelKey);
    }

    // Check if it's a standard agent type
    const agentType = CREATE_AGENT_CATEGORIES_OPTIONS.find(
      (option) => option.value === agent.agent_type
    );
    if (agentType) {
      return t(agentType.labelKey);
    }

    // Fallback to raw value
    return agent.agent_type;
  };

  const handleTriggerAgent = async () => {
    if (isTriggering) {
      return;
    }

    setIsTriggering(true);
    try {
      await triggerAgentRun(agent.id);
    } catch (error) {
      console.error("Failed to trigger agent:", error);
      toast.error(
        t("agents.trigger.errors.updateFailed", {
          defaultValue: "Failed to update cron schedule.",
        })
      );
    } finally {
      setIsTriggering(false);
    }
  };

  const handleToggleSchedule = async () => {
    if (
      isUpdatingSchedule ||
      agent.schedule_type !== AUTONOMOUS_AGENT_STATUS.SCHEDULING
    ) {
      return;
    }

    const isRunning = [
      AGENT_RUN_STATUS.RUNNING,
      AGENT_RUN_STATUS.PENDING,
    ].includes(currentScheduleStatus);
    setIsUpdatingSchedule(true);

    try {
      isRunning ? await stopAgentRun(agent.id) : await startAgentRun(agent.id);

      const nextStatus = isRunning
        ? AGENT_RUN_STATUS.CANCELLED
        : AGENT_RUN_STATUS.RUNNING;

      setCurrentScheduleStatus(nextStatus);
      onScheduleStatusChange?.(agent.id, nextStatus);
    } catch (error) {
      console.error("Failed to update agent schedule status:", error);
      toast.error(t("agents.schedule.errors.updateFailed"));
    } finally {
      setIsUpdatingSchedule(false);
    }
  };

  const getAgentActionButton = () => {
    if (agent?.is_archived)
      return (
        <button
          disabled
          className="cursor-not-allowed rounded-lg bg-red-500 px-4 py-2 text-center text-sm font-medium text-white opacity-50 transition duration-200 dark:bg-red-600"
        >
          {t("agents.archived")}
        </button>
      );

    if (isAutonomous)
      return (
        <div className="flex flex-col gap-2 sm:flex-row">
          {agent.schedule_type === AUTONOMOUS_AGENT_STATUS.SCHEDULING && (
            <button
              onClick={handleToggleSchedule}
              disabled={isUpdatingSchedule}
              className={`rounded-lg px-4 py-2 text-center text-sm font-medium text-white transition duration-200 ${
                isUpdatingSchedule
                  ? "cursor-not-allowed bg-gray-400 opacity-50 dark:bg-gray-600"
                  : [
                      AGENT_RUN_STATUS.RUNNING,
                      AGENT_RUN_STATUS.PENDING,
                    ].includes(currentScheduleStatus)
                  ? "bg-red-400 hover:bg-red-500 dark:bg-red-400 dark:hover:bg-red-500"
                  : "bg-green-400 hover:bg-green-500 dark:bg-green-400 dark:hover:bg-green-500"
              }`}
            >
              {[AGENT_RUN_STATUS.RUNNING, AGENT_RUN_STATUS.PENDING].includes(
                currentScheduleStatus
              )
                ? t("agents.schedule.stop")
                : t("agents.schedule.start")}
            </button>
          )}
          <button
            onClick={handleTriggerAgent}
            disabled={isTriggering}
            className={`rounded-lg px-4 py-2 text-center text-sm font-medium text-white transition duration-200 ${
              isTriggering
                ? "cursor-not-allowed bg-gray-400 opacity-50 dark:bg-gray-600"
                : "bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600"
            }`}
          >
            {t("agents.trigger.title")}
          </button>
        </div>
      );

    return (
      <Link
        to={`/chat/${agent.id}`}
        className="rounded-lg bg-brand-900 px-4 py-2 text-center text-sm font-medium text-white transition duration-200 hover:bg-brand-800 dark:bg-brand-400 dark:text-gray-900 dark:hover:bg-brand-300"
      >
        {t("agents.chatWithAgent")}
      </Link>
    );
  };
  return (
    <Card
      extra={`flex flex-col w-full h-full !p-5 3xl:p-![18px] bg-white dark:bg-gray-900 ${
        extra || ""
      }`}
    >
      <div className="flex h-full w-full flex-col">
        {/* Image Section */}
        <div className="relative mb-4 w-full">
          <Image
            src={agent.icon_uri}
            fallbackSrc={Nft2}
            className="h-48 w-full rounded-xl bg-gray-100 object-cover dark:bg-gray-800"
            alt={agent.name}
          />
        </div>

        {/* Content Section */}
        <div className="flex flex-1 flex-col">
          {/* Header with Title and Status */}
          <div className="mb-4 flex items-start justify-between">
            <div className="flex-1">
              <h3 className="mb-1 text-lg font-bold text-navy-700 dark:text-white">
                {agent.name}
              </h3>
              {agent.agent_type && (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {t("agents.type")}:
                  </span>
                  <span
                    className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
                      isOrchestrator
                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                        : isAutonomous
                        ? "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
                        : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {getAgentTypeLabel()}
                  </span>
                </div>
              )}
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {t("agents.lastUpdated")}{" "}
                {new Date(
                  agent.updated_at || agent.created_at
                ).toLocaleDateString()}
              </p>
            </div>
            <span
              className={`ml-3 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium text-white dark:text-gray-100 ${getAgentStatusColor(
                agent.status
              )}`}
            >
              {t(getAgentStatusTranslationKey(agent.status))}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="mt-auto flex flex-col gap-2 sm:flex-row sm:justify-between">
            <Link
              to={`/dashboard/agents/info/${agent.id}`}
              className="rounded-lg px-4 py-2 text-center text-sm font-medium text-brand-900 transition duration-200 dark:text-brand-400"
            >
              {t("agents.viewDetails")}
            </Link>
            {getAgentActionButton()}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AgentCard;
